import { CheckOutlined, SaveOutlined, WarningOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Input, Row, Table, Modal } from "antd";
import { get, indexOf, isEmpty, lowerCase, toLower } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Button from "../../../../../../components/functional_components/Button/PFButton";
import { setLoading } from "../../../../../../redux/ducks/Loading";
import { setProject } from "../../../../../../redux/ducks/ProjectManagement";
import { refreshData } from "../../../../../../redux/ducks/BillingPlanManagement";
import Project from "../../../../../../services/api/Project";
import ProjectInvoicePlan from "../../../../../../services/api/ProjectInvoicePlan";
import ProjectPlan from "../../../../../../services/api/ProjectPlan";
import { mapMonths } from "../../../../../../utils/properties";
import { formatNumber, getClassName, showError, showSuccess, unformatNumber } from "../../../../../../utils/utilities";

const { confirm } = Modal;

const BillingTable = (props) => {
  const tableRef = useRef(null);
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const projectManagement = useSelector((state) => get(state.ProjectManagementDuck, "project", {}));
  const openPlanning = useSelector((state) => get(state.ProjectManagementDuck, "openPlanning", null));
  const isRefreshData = useSelector((state) => state.billingPlanManagementDuck.refreshData);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const [state, setState] = useState({
    columnsInvoicePlan: [],
    customFormBilling: [],
    customFormAdj: null,
    refresh: false,
    dataSource: [],
  });

  useEffect(() => {
    if (!isEmpty(props.allData) && !isEmpty(props.dataContentBilling)) {
      setState((prevState) => ({ ...prevState, dataSource: props.dataContentBilling }));
      columns(props.allData, props.dataContentBilling);
    }
    if (isRefreshData) {
      dispatch(refreshData(false));
    }
    if (tableRef.current && !isEmpty(props.allData)) {
      const columnNode = Array.from(tableRef.current.querySelectorAll("th")).filter(
        (th) => th.textContent == props.allData.openYear && th.classList.contains("ant-table-cell")
      );
      if (columnNode[0]) {
        tableRef.current.querySelector(".ant-table-content").scrollLeft = columnNode[0].offsetLeft;
      }
    }
  }, [props, currentLanguage, tableRef.current]);

  const columns = (allData, trueData) => {
    let objCol = [
      {
        title: t("projectsManagement.planning.billingPlan.activityResource"),
        width: 200,
        fixed: "left",
        render: (record) => t(`projectsManagement.planning.billingPlan.${record.type}`),
      },
    ];

    trueData[0].yearValues.forEach((yv, i) => {
      let childrenArr = [];
      Object.keys(yv.columnValueMap).forEach((cvm) => {
        if (indexOf(["key", "isOnlyVisual", "isHour", "activity"], cvm) === -1) {
          if (mapMonths[lowerCase(cvm)] !== undefined || cvm === "PRESENT") {
            const timeMonth = getClassName(cvm, allData.openMonth, allData.openYear, yv.year);
            // Months
            if (cvm !== "PRESENT") {
              // Print column only if not "PRESENT" column
              if (timeMonth === "present") {
                // If I'm in current month, I print the double column
                if (allData.closed) {
                  childrenArr.push({
                    title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`),
                    width: 100,
                    className: "table-value-right past",
                    dataIndex: ["consValueOpenMonth"],
                    render: (amount) =>
                      formatNumber({
                        value: amount ? amount : 0,
                        suffix: "EUR",
                        lang: currentLanguage,
                        decimalScale: 0,
                      }),
                  });
                } else {
                  childrenArr.push({
                    title: `${t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`)} plan`,
                    width: 100,
                    dataIndex: ["yearValues", i, "columnValueMap", cvm],
                    className: "table-value-right",
                    render: (amount, record) =>
                      formatNumber({
                        value: record.type === "INVOICE_PLAN" ? amount : "-",
                        suffix: "EUR",
                        lang: currentLanguage,
                        decimalScale: 0,
                      }),
                  });
                  childrenArr.push({
                    title: `${t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`)} act`,
                    width: 100,
                    dataIndex: ["consValueOpenMonth"],
                    className: `table-value-right ${timeMonth}`,
                    render: (amount, record) =>
                      formatNumber({
                        name: cvm,
                        value: amount ? amount : 0,
                        lang: currentLanguage,
                        id: cvm,
                        displayType: showInput(
                          "displayType",
                          timeMonth,
                          userInfo.canEditPlanning,
                          record.type,
                          allData.closed
                        ),
                        decimalScale: showInput(
                          "decimalScale",
                          timeMonth,
                          userInfo.canEditPlanning,
                          record.type,
                          allData.closed
                        ),
                        suffix: "EUR",
                        customInput: Input,
                        onChange: (e) => handleChange(e, mapMonths[lowerCase(cvm)], yv.year, record.type),
                      }),
                  });
                }
              } else {
                childrenArr.push({
                  title: t(`projectsManagement.planning.workCostsTab.${toLower(cvm)}`),
                  width: 100,
                  dataIndex: ["yearValues", i, "columnValueMap", cvm],
                  className: `table-value-right ${timeMonth}`,
                  render: (amount, record) =>
                    formatNumber({
                      name: cvm,
                      value: amount ? amount : 0,
                      lang: currentLanguage,
                      id: cvm,
                      displayType: showInput(
                        "displayType",
                        timeMonth,
                        userInfo.canEditPlanning,
                        record.type,
                        allData.closed
                      ),
                      decimalScale: showInput(
                        "decimalScale",
                        timeMonth,
                        userInfo.canEditPlanning,
                        record.type,
                        allData.closed
                      ),
                      suffix: "EUR",
                      customInput: Input,
                      onChange: (e) => handleChange(e, mapMonths[lowerCase(cvm)], yv.year, record.type),
                    }),
                });
              }
            }
          }
        }
      });
      objCol.push({
        title: yv.year,
        width: 100,
        className: "table-value-right",
        children: childrenArr,
      });
    });

    // Total, total-1 total budget
    objCol.push({
      title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT")}`),
      width: 100,
      dataIndex: ["totalValues", "TOT"],
      fixed: "right",
      className: "table-value-right highlighted",
      render: (amount) =>
        formatNumber({ value: amount ? amount : 0, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
    });

    objCol.push({
      title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_PREV_MONTH")}`),
      width: 100,
      dataIndex: ["totalValues", "TOT_PREV_MONTH"],
      fixed: "right",
      className: "table-value-right highlighted",
      render: (amount) =>
        formatNumber({ value: amount ? amount : 0, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
    });

    objCol.push({
      title: t(`projectsManagement.planning.workCostsTab.${toLower("TOT_BUDGET")}`),
      width: 100,
      dataIndex: ["totalValues", "TOT_BUDGET"],
      fixed: "right",
      className: "table-value-right highlighted",
      render: (amount) =>
        formatNumber({ value: amount ? amount : 0, suffix: "EUR", lang: currentLanguage, decimalScale: 0 }),
    });

    setState((prevState) => ({ ...prevState, columnsInvoicePlan: objCol }));
  };

  const showInput = (type, timeMonth, canEdit, activity, closed) => {
    let displayType = "text";
    let decimalScale = 0;
    if (
      (timeMonth === "future" && canEdit && activity === "INVOICE_PLAN" && !closed && projectManagement.enabled) ||
      (timeMonth === "future" &&
        canEdit &&
        activity === "INVOICE_PLAN" &&
        closed &&
        openPlanning &&
        projectManagement.enabled) ||
      (timeMonth === "present" && activity === "ADJUSTMENT_INVOICE_PLAN" && !closed && projectManagement.enabled)
    ) {
      displayType = "input";
      decimalScale = 2;
    }
    return type === "displayType" ? displayType : decimalScale;
  };

  const handleChange = (e, month, year, activity) => {
    const amount = unformatNumber(e.target.value, currentLanguage, "amount");

    const newDataSource = [...state.dataSource];
    const changedTableRowIndex = newDataSource.findIndex((row) => row.type === activity);
    const changedYearIndex = newDataSource[changedTableRowIndex].yearValues.findIndex((yv) => yv.year === year);

    if (activity === "INVOICE_PLAN") {
      let customFormBilling = state.customFormBilling;
      // Update changed form values
      if (isEmpty(customFormBilling)) {
        customFormBilling.push({ year, month, amount });
      } else {
        const foundIndex = customFormBilling.findIndex((item) => item.year === year && item.month === month);
        if (foundIndex !== -1) {
          customFormBilling[foundIndex].amount = amount;
        } else {
          customFormBilling.push({ year, month, amount });
        }
      }
      // Update table dataSource values
      for (let cv in newDataSource[changedTableRowIndex].yearValues[changedYearIndex].columnValueMap) {
        if (mapMonths[lowerCase(cv)] === month) {
          newDataSource[changedTableRowIndex].yearValues[changedYearIndex].columnValueMap[cv] = amount;
        }
      }
      setState((prevState) => ({ ...prevState, customFormBilling, dataSource: newDataSource }));
    } else {
      // Update changed form values
      const customFormAdj = { year, month, amount };
      // Update table dataSource values
      newDataSource[changedTableRowIndex].consValueOpenMonth = amount;
      setState((prevState) => ({ ...prevState, customFormAdj, dataSource: newDataSource }));
    }
  };

  const projectDetail = async (id) => {
    dispatch(setLoading(true));
    const infoProject = await Project.getInfoProject(id);
    if (!infoProject.error) {
      dispatch(setProject(get(infoProject, "data", {})));
    } else {
      showError(get(infoProject, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const handleSave = async () => {
    let error = true;
    let errorAdj = true;
    let errorResponse = {};
    dispatch(setLoading(true));
    if (userInfo.canEditPlanning && !isEmpty(state.customFormBilling)) {
      // invoice
      const response = await ProjectInvoicePlan.saveDataProjectInvoicePlan(params.id, state.customFormBilling);
      if (!response.error) {
        error = false;
        setState((prevState) => ({ ...prevState, customFormBilling: [] }));
      } else {
        errorResponse = response;
      }
    }
    if (userInfo.canEditAdjPlanning && state.customFormAdj) {
      // adjustment
      const response = await ProjectInvoicePlan.saveDataProjectAdjInvoicePlan(params.id, [state.customFormAdj]);
      if (!response.error) {
        errorAdj = false;
        setState((prevState) => ({ ...prevState, customFormAdj: null }));
      } else {
        errorResponse = response;
      }
    }
    if (!(error && errorAdj)) {
      showSuccess();
      dispatch(refreshData(true));
    } else {
      showError(get(errorResponse, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const handleRelease = () => {
    confirm({
      title: t("projectsManagement.planning.billingPlan.ensureApprove"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        dispatch(setLoading(true));
        const response = await ProjectPlan.approveProjectPlan(params.id, "planType=INVOICE_PLAN");
        if (!response.error) {
          showSuccess();
          projectDetail(params.id);
          dispatch(refreshData(true));
        } else {
          showError(get(response, "error.messageCode", "ER000"));
        }
        dispatch(setLoading(false));
      },
      onCancel() {},
    });
  };

  const checkSave = () => {
    // Here I check if I can edit only planning or only adjustment or both
    let ck = true;
    if (userInfo.canEditPlanning && userInfo.canEditAdjPlanning) {
      // both
      ck = !((!isEmpty(state.customFormBilling) || state.customFormAdj) && projectManagement.enabled);
    } else if (userInfo.canEditPlanning) {
      // only planning
      ck = !(!isEmpty(state.customFormBilling) && projectManagement.enabled);
    } else if (userInfo.canEditAdjPlanning) {
      // only adjustment
      ck = !(state.customFormAdj && projectManagement.enabled);
    }
    return ck;
  };

  const checkRelease = () => {
    // Here I check if I can edit only planning or only adjustment or both
    let ck = true;
    if (userInfo.canEditPlanning && userInfo.canEditAdjPlanning) {
      // both
      ck = !(
        !(!isEmpty(state.customFormBilling) || state.customFormAdj) &&
        projectManagement.enabled &&
        !props.allData.closed &&
        !props.allData.alert
      );
    } else if (userInfo.canEditPlanning) {
      // only planning
      ck = !(
        !!isEmpty(state.customFormBilling) &&
        projectManagement.enabled &&
        !props.allData.closed &&
        !props.allData.alert
      );
    } else if (userInfo.canEditAdjPlanning) {
      // only adjustment
      ck = !(!state.customFormAdj && projectManagement.enabled && !props.allData.closed && !props.allData.alert);
    }
    return ck;
  };

  return (
    <>
      <div ref={tableRef}>
        <Table
          bordered
          dataSource={state.dataSource}
          columns={state.columnsInvoicePlan}
          size="small"
          pagination={false}
          scroll={{ x: 1500 }}
        />
      </div>
      <Row type="flex" align="middle" justify="end" className="mb-10 mt-10">
        {props.allData.alert && <WarningOutlined className="PFred mr-10 icon-medium" />}
        <Button
          isDisabled={checkRelease()}
          currentIcon={<CheckOutlined className="icon-medium" />}
          className="ml-10"
          clickCallback={handleRelease}
        />
        <Button
          isDisabled={checkSave()}
          currentIcon={<SaveOutlined className="icon-medium" />}
          className="ml-10"
          clickCallback={handleSave}
        />
      </Row>
    </>
  );
};

export default BillingTable;
